
import { Component, Vue } from 'vue-property-decorator'
import Empty from '@/components/Empty/index.vue'
import ImageUpload from '@/components/ImgUpload/index.vue'
import {
  getStorePageByEmp,
  addStore,
  updateStore,
  deleteStore,
  getStoreInfo
} from '@/api/store'
import { getDishPage } from '@/api/dish'
import { UserModule } from '@/store/modules/user'
import { Form as ElForm } from 'element-ui'

const emptyForm = () => ({
  storeName: '',
  storePhone: '',
  provinceName: '',
  cityName: '',
  districtName: '',
  detail: '',
  latitude: '',
  longitude: '',
  logoUrl: '',
  status: 1,
  assignMode: 1,
  managerAcceptDelivery: 0,
  emptyBucketDepositDishId: null as number | null
})

@Component({
  name: 'StoreManage',
  components: {
    Empty,
    ImageUpload
  }
})
export default class extends Vue {
  private storeName: string = ''
  private status: number | null = null
  private counts: number = 0
  private page: number = 1
  private pageSize: number = 10
  private tableData: any[] = []
  private isSearch: boolean = false

  private dialogVisible: boolean = false
  private dialogTitle: string = '添加商户'
  private isEdit: boolean = false
  private currentId: string | number | null = null
  private submitLoading: boolean = false

  private form: any = emptyForm()
  private depositDishOptions: any[] = []
  private depositDishLoading: boolean = false

  get isSuperAdmin() {
    return UserModule.isSuperAdmin
  }

  private depositDishLabel(d: any) {
    if (!d) return ''
    const price = d.price != null ? Number(d.price) : NaN
    const priceText = Number.isFinite(price) ? `¥${price.toFixed(2)}` : ''
    const statusText = d.status === 0 ? '（停售）' : ''
    return [d.name, priceText, statusText].filter(Boolean).join(' ')
  }

  private async loadDepositDishOptions(storeId: string | number | null) {
    this.depositDishOptions = []
    if (storeId == null || storeId === '') return
    this.depositDishLoading = true
    try {
      const res: any = await getDishPage({
        page: 1,
        pageSize: 200,
        storeId
      })
      const records =
        (res && res.data && res.data.data && res.data.data.records) ||
        (res && res.data && res.data.records) ||
        []
      this.depositDishOptions = Array.isArray(records) ? records : []
    } catch (e) {
      this.depositDishOptions = []
    } finally {
      this.depositDishLoading = false
    }
  }

  get rules(): any {
    return {
      storeName: [{ required: true, message: '请输入商户名称', trigger: 'blur' }],
      storePhone: [
        { required: true, message: '请输入联系电话', trigger: 'blur' },
        { pattern: /^1\d{10}$/, message: '请输入11位手机号', trigger: 'blur' }
      ],
      provinceName: [{ required: true, message: '请填写省', trigger: 'blur' }],
      cityName: [{ required: true, message: '请填写市', trigger: 'blur' }],
      districtName: [{ required: true, message: '请填写区/县', trigger: 'blur' }],
      detail: [{ required: true, message: '请填写门牌地址', trigger: 'blur' }],
      longitude: [
        {
          validator: (_rule: any, value: any, callback: Function) => {
            const lngStr = value != null && value !== '' ? String(value).trim() : ''
            if (!lngStr) {
              callback(new Error('请填写经度'))
              return
            }
            if (!/^-?\d+(\.\d{1,18})?$/.test(lngStr)) {
              callback(new Error('经度格式不正确，小数最多 18 位'))
              return
            }
            const lng = Number(lngStr)
            if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
              callback(new Error('经度需在 -180～180 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      latitude: [
        {
          validator: (_rule: any, value: any, callback: Function) => {
            const latStr = value != null && value !== '' ? String(value).trim() : ''
            if (!latStr) {
              callback(new Error('请填写纬度'))
              return
            }
            if (!/^-?\d+(\.\d{1,18})?$/.test(latStr)) {
              callback(new Error('纬度格式不正确，小数最多 18 位'))
              return
            }
            const lat = Number(latStr)
            if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
              callback(new Error('纬度需在 -90～90 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      status: [{ required: true, message: '请选择状态', trigger: 'change' }]
    }
  }

  created() {
    this.init()
  }

  private displayAddress(row: any) {
    if (row.address && String(row.address).trim()) {
      return row.address
    }
    const parts = [row.provinceName, row.cityName, row.districtName, row.detail]
      .filter((p) => p && String(p).trim() && p !== '待完善')
    return parts.length ? parts.join('') : '—'
  }

  private onLogoChange(url: string) {
    this.form.logoUrl = url || ''
  }

  private initSearch() {
    this.page = 1
    this.init(true)
  }

  private async init(isSearch?: boolean) {
    this.isSearch = !!isSearch
    const params: any = {
      page: this.page,
      pageSize: this.pageSize
    }
    if (this.storeName) {
      params.storeName = this.storeName
    }
    if (this.status !== null && this.status !== undefined) {
      params.status = this.status
    }
    try {
      const res: any = await getStorePageByEmp(params)
      if (String(res.data.code) === '1') {
        const data = res.data.data
        this.tableData = (data && data.records) || data || []
        this.counts = (data && data.total) || this.tableData.length
      } else {
        this.$message.error(res.data.msg || '获取商户列表失败')
      }
    } catch (err) {
      this.$message.error('请求出错了：' + (err as Error).message)
    }
  }

  private handleSizeChange(val: number) {
    this.pageSize = val
    this.init()
  }

  private handleCurrentChange(val: number) {
    this.page = val
    this.init()
  }

  /** 仅有 address 时尝试拆成省市区+门牌，便于编辑旧数据 */
  private parseAddressParts(addr: string) {
    const s = (addr || '').trim()
    if (!s) return null
    const m = s.match(
      /^(.+?(?:省|自治区|特别行政区))(.+?(?:市|自治州|地区|盟))?(.+?(?:区|县|旗))?([\s\S]*)$/
    )
    if (!m) return { detail: s }
    return {
      provinceName: (m[1] || '').trim(),
      cityName: (m[2] || '').trim(),
      districtName: (m[3] || '').trim(),
      detail: (m[4] || '').trim()
    }
  }

  private fillFormFromRow(row: any) {
    const norm = (v: any) => (v === '待完善' ? '' : (v || ''))
    let provinceName = norm(row.provinceName)
    let cityName = norm(row.cityName)
    let districtName = norm(row.districtName)
    let detail = norm(row.detail)
    const fullAddr = norm(row.address)

    if (!provinceName && !cityName && !districtName && !detail && fullAddr) {
      const parsed = this.parseAddressParts(fullAddr)
      if (parsed) {
        provinceName = parsed.provinceName || provinceName
        cityName = parsed.cityName || cityName
        districtName = parsed.districtName || districtName
        detail = parsed.detail || detail
      }
    }

    this.form = {
      storeName: row.storeName || '',
      storePhone: row.storePhone || '',
      provinceName,
      cityName,
      districtName,
      detail,
      latitude: row.latitude != null ? String(row.latitude) : '',
      longitude: row.longitude != null ? String(row.longitude) : '',
      logoUrl: row.logoUrl || '',
      status: row.status != null ? row.status : 1,
      assignMode: row.assignMode != null ? Number(row.assignMode) : 1,
      managerAcceptDelivery: row.managerAcceptDelivery != null ? Number(row.managerAcceptDelivery) : 0,
      emptyBucketDepositDishId:
        row.emptyBucketDepositDishId != null ? Number(row.emptyBucketDepositDishId) : null
    }
  }

  private async openEditDialog(type: 'add' | 'edit', row?: any) {
    if (type === 'add') {
      this.dialogTitle = '添加商户'
      this.isEdit = false
      this.currentId = null
      this.form = emptyForm()
      this.depositDishOptions = []
    } else {
      this.dialogTitle = '编辑商户'
      this.isEdit = true
      this.currentId = row.id
      this.fillFormFromRow(row)
      try {
        const res: any = await getStoreInfo(row.id)
        if (String(res.data.code) === '1' && res.data.data) {
          this.fillFormFromRow(res.data.data)
        }
      } catch (e) {
        // 列表数据已填充，忽略详情拉取失败
      }
      await this.loadDepositDishOptions(row.id)
    }
    this.dialogVisible = true
    this.$nextTick(() => {
      const ref = this.$refs.formRef as ElForm
      if (ref) ref.clearValidate()
    })
  }

  private onDialogClosed() {
    this.form = emptyForm()
    this.submitLoading = false
  }

  private buildPayload() {
    const f = this.form
    const province = (f.provinceName || '').trim()
    const city = (f.cityName || '').trim()
    const district = (f.districtName || '').trim()
    const detail = (f.detail || '').trim()
    const address = [province, city, district, detail].filter(Boolean).join('')

    const latStr = (f.latitude != null ? String(f.latitude) : '').trim()
    const lngStr = (f.longitude != null ? String(f.longitude) : '').trim()
    if (!latStr || !lngStr) {
      throw new Error('请填写纬度和经度')
    }
    if (!/^-?\d+(\.\d{1,18})?$/.test(latStr)) {
      throw new Error('纬度格式不正确，小数最多 18 位')
    }
    if (!/^-?\d+(\.\d{1,18})?$/.test(lngStr)) {
      throw new Error('经度格式不正确，小数最多 18 位')
    }
    const latitude = Number(latStr)
    const longitude = Number(lngStr)
    if (isNaN(latitude) || latitude < -90 || latitude > 90) {
      throw new Error('纬度需在 -90～90 之间')
    }
    if (isNaN(longitude) || longitude < -180 || longitude > 180) {
      throw new Error('经度需在 -180～180 之间')
    }

    return {
      id: this.isEdit ? this.currentId : undefined,
      storeName: (f.storeName || '').trim(),
      storePhone: (f.storePhone || '').trim(),
      provinceName: province || undefined,
      cityName: city || undefined,
      districtName: district || undefined,
      detail: detail || undefined,
      address: address || undefined,
      // 以字符串提交，后端按 BigDecimal 精确解析，避免浮点截断
      latitude: latStr,
      longitude: lngStr,
      logoUrl: (f.logoUrl || '').trim() || undefined,
      status: f.status,
      assignMode: f.assignMode != null ? Number(f.assignMode) : 1,
      managerAcceptDelivery: f.managerAcceptDelivery != null ? Number(f.managerAcceptDelivery) : 0,
      emptyBucketDepositDishId:
        f.emptyBucketDepositDishId != null && f.emptyBucketDepositDishId !== ''
          ? Number(f.emptyBucketDepositDishId)
          : null
    }
  }

  private submitForm() {
    (this.$refs.formRef as ElForm).validate(async (valid: boolean) => {
      if (!valid) return
      this.submitLoading = true
      let payload: any
      try {
        payload = this.buildPayload()
      } catch (e) {
        this.submitLoading = false
        this.$message.warning((e as Error).message)
        return
      }
      try {
        const api = this.isEdit ? updateStore : addStore
        const res: any = await api(payload)
        if (String(res.data.code) === '1') {
          this.$message.success(this.isEdit ? '编辑成功' : '添加成功')
          this.dialogVisible = false
          window.dispatchEvent(new Event('shop-status-changed'))
          this.init()
        } else {
          this.$message.error(res.data.msg || '操作失败')
        }
      } catch (err) {
        this.$message.error('请求出错了：' + (err as Error).message)
      } finally {
        this.submitLoading = false
      }
    })
  }

  private deleteStoreHandle(row: any) {
    this.$confirm('确认删除该商户？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(async () => {
        try {
          const res: any = await deleteStore(row.id)
          if (String(res.data.code) === '1') {
            this.$message.success('删除成功')
            this.init()
          } else {
            this.$message.error(res.data.msg || '删除失败')
          }
        } catch (err) {
          this.$message.error('请求出错了：' + (err as Error).message)
        }
      })
      .catch(() => {})
  }

  private async selectStore(row: any) {
    await UserModule.SetStore(String(row.id))
    this.$message.success(`已切换到商户：${row.storeName}`)
    this.$router.push('/dashboard')
  }
}
